/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

import { Component, ReactNode, ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, errorInfo: ErrorInfo, retry: () => void, goBack?: () => void) => ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  context?: string; // e.g., "Story Step 2" for better logging
  shell?: 'expert' | 'customer'; // Shell mode for error handling
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
}

/**
 * ErrorBoundary Component
 * 
 * Catches React errors in child components and displays a fallback UI
 * instead of crashing the entire app with a white screen.
 * 
 * Usage:
 * <ErrorBoundary context="Story Step 2">
 *   <YourComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render shows the fallback UI
    return {
      hasError: true,
      error,
      errorId: `ERR_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details
    const { context, onError } = this.props;
    const errorId = this.state.errorId || 'UNKNOWN';

    console.error(`[ErrorBoundary${context ? ` - ${context}` : ''}] Error caught:`, {
      errorId,
      error,
      errorInfo,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    // Store error info in state
    this.setState({ errorInfo });

    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }

    // In development, show more details
    if (process.env.NODE_ENV === 'development') {
      console.group(`🔴 Error Details (${errorId})`);
      console.log('Context:', context || 'Unknown');
      console.log('Error:', error);
      console.log('Component Stack:', errorInfo.componentStack);
      console.groupEnd();
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  };

  handleGoBack = () => {
    window.history.back();
  };

  handleRestart = () => {
    // Clear state and reload
    window.location.href = '/story';
  };

  render() {
    const { hasError, error, errorInfo, errorId } = this.state;
    const { children, fallback, context, shell } = this.props;

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback(error, errorInfo!, this.handleRetry, this.handleGoBack);
      }

      // Determine if we should show detailed error info
      // Expert mode: Always show details
      // Customer mode: Only show friendly message (log details for telemetry)
      const showDetails = shell === 'expert' || process.env.NODE_ENV === 'development';

      // In customer mode, log error details for telemetry but don't display
      if (shell === 'customer' && process.env.NODE_ENV !== 'development') {
        // TODO: Send to telemetry service
        console.error('[Customer Mode Error]', {
          errorId,
          message: error.message,
          stack: error.stack,
          context,
        });
      }

      // Default fallback UI
      return (
        <div style={{
          padding: '2rem',
          maxWidth: '600px',
          margin: '2rem auto',
          background: '#fef2f2',
          border: '2px solid #fca5a5',
          borderRadius: '12px',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1rem',
          }}>
            <span style={{ fontSize: '2rem' }}>⚠️</span>
            <h2 style={{
              margin: 0,
              fontSize: '1.5rem',
              color: '#991b1b',
            }}>
              Something went wrong
            </h2>
          </div>

          <p style={{
            color: '#7f1d1d',
            marginBottom: '1rem',
            lineHeight: '1.5',
          }}>
            {shell === 'customer' && process.env.NODE_ENV !== 'development'
              ? 'We encountered an unexpected issue. Your progress has been saved. Please try again or go back to continue.'
              : context 
                ? `An error occurred on this page (${context}). Don't worry—your progress is saved.`
                : 'An error occurred on this page. Don\'t worry—your progress is saved.'
            }
          </p>

          {showDetails && (
            <details style={{
              marginBottom: '1rem',
              padding: '0.75rem',
              background: '#fff',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
            }}>
              <summary style={{
                cursor: 'pointer',
                fontWeight: 600,
                color: '#991b1b',
                marginBottom: '0.5rem',
              }}>
                {shell === 'expert' ? 'Error Details (Expert Mode)' : 'Debug Info (Dev Only)'}
              </summary>
              <div style={{
                fontSize: '0.875rem',
                fontFamily: 'monospace',
                color: '#7f1d1d',
                marginTop: '0.5rem',
              }}>
                <div><strong>Error ID:</strong> {errorId}</div>
                <div><strong>Message:</strong> {error.message}</div>
                {error.stack && (
                  <pre style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem',
                    background: '#fef2f2',
                    borderRadius: '4px',
                    overflow: 'auto',
                    fontSize: '0.75rem',
                  }}>
                    {error.stack}
                  </pre>
                )}
                {errorInfo && errorInfo.componentStack && (
                  <>
                    <div style={{ marginTop: '0.5rem' }}><strong>Component Stack:</strong></div>
                    <pre style={{
                      marginTop: '0.25rem',
                      padding: '0.5rem',
                      background: '#fef2f2',
                      borderRadius: '4px',
                      overflow: 'auto',
                      fontSize: '0.75rem',
                    }}>
                      {errorInfo.componentStack}
                    </pre>
                  </>
                )}
              </div>
            </details>
          )}

          <div style={{
            display: 'flex',
            gap: '0.75rem',
            flexWrap: 'wrap',
          }}>
            <button
              onClick={this.handleGoBack}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#4b5563'}
              onMouseOut={(e) => e.currentTarget.style.background = '#6b7280'}
            >
              ← Go Back
            </button>
            <button
              onClick={this.handleRetry}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#1d4ed8'}
              onMouseOut={(e) => e.currentTarget.style.background = '#2563eb'}
            >
              🔄 Try Again
            </button>
            <button
              onClick={this.handleRestart}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1rem',
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#b91c1c'}
              onMouseOut={(e) => e.currentTarget.style.background = '#dc2626'}
            >
              ↺ Restart Story
            </button>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
